import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { consultFeltMediatorService, persistAiSummary } from '../../../services/AiMediatorService';
import TextArea from '../../TextArea/TextArea';
import SimpleToggleSwitch from '../../SimpleToggleSwitch/SimpleToggleSwitch';
import ConflictSummary from '../../ConflictSummary/ConflictSummary';
import './Recap.scss';

function Recap({updateValue, mirrorData, name, partnerName, side1Animal, side1Gender, side2Animal, side2Gender, relation, relationship}) {
    const [isLoadingRecap, setIsLoadingRecap] = useState(true);
    const [loadingPhase, setLoadingPhase] = useState('loading'); // loading | slow | timeout
    const [localName, setLocalName] = useState('');
    const [localDescription, setLocalDescription] = useState('');
    const [problems, setProblems] = useState([]);
    const {t, i18n} = useTranslation();
    const [showSummary, setShowSummary] = useState(false);
    useEffect(() => {
      let didCancel = false;
      let slowTimer = null;
      let timeoutTimer = null;
  
      const run = async () => {
        console.log('Recap loading: start');
  
        const existingAISummary = mirrorData?.journeyProps?.AISummary || {};
        const hasName = !!(existingAISummary?.mirrorName || '').trim();
        const hasDesc = !!(existingAISummary?.mirrorDescription || '').trim();
        const hasProblems = Array.isArray(existingAISummary?.relationshipProblems) && existingAISummary.relationshipProblems.length > 0;
        console.log('Recap cache check:', { hasName, hasDesc, hasProblems, problemsCount: (existingAISummary?.relationshipProblems || []).length });
  
        if (hasName && hasDesc && hasProblems) {
          // Use existing data, skip AI
          if (didCancel) return;
          setLocalName(existingAISummary.mirrorName);
          setLocalDescription(existingAISummary.mirrorDescription);
          setProblems(Array.isArray(existingAISummary.relationshipProblems) ? existingAISummary.relationshipProblems : []);
          console.log('Recap cached problems count:', Array.isArray(existingAISummary.relationshipProblems) ? existingAISummary.relationshipProblems.length : 0);
          setIsLoadingRecap(false);
          // Ensure recapDone is marked
          if (!existingAISummary.recapDone && updateValue) {
            console.log('Recap: marking recapDone=true (cached)');
            updateValue(true);
          }
          console.log('Recap loading: finish (from cache)');
          return;
        }
  
        // No cached data: mark recap as not done to disable CTA while loading
        if (updateValue) {
          console.log('Recap: marking recapDone=false (loading started)');
          updateValue(false);
        }
  
        // Start safety timers
        slowTimer = setTimeout(() => {
          if (!didCancel) setLoadingPhase('slow');
        }, 2 * 60 * 1000);
        timeoutTimer = setTimeout(() => {
          if (!didCancel) setLoadingPhase('timeout');
        }, 3 * 60 * 1000);
  
        let success = false;
        try {
          const result = await consultFeltMediatorService({
            mirrorData,
            name,
            partnerName,
            side1Animal,
            side1Gender,
            side2Animal,
            side2Gender,
            relation,
            language: i18n?.language || 'en',
            fallbackPartnerName: partnerName
          });
          if (didCancel) return;
          const existingAISummary2 = mirrorData?.journeyProps?.AISummary || {};
          const updatedAISummary = persistAiSummary({ existingAISummary: existingAISummary2, result });
          setLocalName(updatedAISummary.mirrorName || '');
          setLocalDescription(updatedAISummary.mirrorDescription || '');
          setProblems(Array.isArray(updatedAISummary.relationshipProblems) ? updatedAISummary.relationshipProblems : []);
          if (updateValue) {
            updateValue({ __updateAISummary: updatedAISummary, __recapDone: false });
          }
          console.log('Recap AI problems count:', Array.isArray(result.relationshipProblems) ? result.relationshipProblems.length : 0);
          success = true;
        } catch (e) {
          console.error('Recap mediator failed', e);
          if (!didCancel) setLoadingPhase('timeout');
        } finally {
          if (didCancel) return;
          if (success) {
            setIsLoadingRecap(false);
            console.log('Recap loading: finish');
            if (updateValue) {
              console.log('Recap: marking recapDone=true (AI finished)');
              updateValue({ __updateAISummary: null, __recapDone: true });
            }
          }
        }
      };
  
      run();
  
      return () => {
        didCancel = true;
        if (slowTimer) clearTimeout(slowTimer);
        if (timeoutTimer) clearTimeout(timeoutTimer);
      };
    }, []);
  
    const onChangeName = (val) => {
      setLocalName(val);
      const existingAISummary = mirrorData?.journeyProps?.AISummary || {};
      const updated = {
        ...existingAISummary,
        mirrorName: val,
        mirrorDescription: localDescription
      };
      if (updateValue) updateValue({ __updateAISummary: updated });
    };
    const onChangeDescription = (val) => {
      setLocalDescription(val);
      const existingAISummary = mirrorData?.journeyProps?.AISummary || {};
      const updated = {
        ...existingAISummary,
        mirrorName: localName,
        mirrorDescription: val
      };
      if (updateValue) updateValue({ __updateAISummary: updated });
    };
  
    return (
      <div className="recapStep">
        {isLoadingRecap ? (
          <>
            <div className="answerAI">
              <div className="typing">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
            <p className="conflict-name"><strong>{t('recap.loadingTitle')}</strong></p>
            <p className="conflict-description">{t('recap.loadingDescription')}</p>
            {loadingPhase === 'slow' && (
              <p className="conflict-description">{t('recap.slowWarning')}</p>
            )}
            {loadingPhase === 'timeout' && (
              <p className="conflict-description">{t('recap.timeoutError')}</p>
            )}
          </>
        ) : (
          <div className="recap-content">
            <div className="miniPanel">
              <TextArea
                textAreaClasses=""
                labelClasses="form-label-title"
                label={t('recap.writeSummary')}
                rows={4}
                value={localDescription}
                type="text"
                id="recapMirrorDescription"
                name="recapMirrorDescription"
                onChangeTextArea={onChangeDescription}
                isMandatory={false}
                handleBlur={() => {
                  const existingAISummary = mirrorData?.journeyProps?.AISummary || {};
                  const updated = { ...existingAISummary, mirrorName: localName, mirrorDescription: localDescription };
                  console.log('Recap blur: saving mirrorDescription', updated);
                  if (updateValue) updateValue({ __updateAISummary: updated });
                }}
              />
            </div>
            <div className="miniPanel">
              <TextArea
                textAreaClasses=""
                labelClasses="form-label-title"
                label={t('recap.feltName')}
                rows={1}
                value={localName}
                type="text"
                id="recapMirrorName"
                name="recapMirrorName"
                onChangeTextArea={onChangeName}
                isMandatory={false}
                handleBlur={() => {
                  const existingAISummary = mirrorData?.journeyProps?.AISummary || {};
                  const updated = { ...existingAISummary, mirrorName: localName, mirrorDescription: localDescription };
                  console.log('Recap blur: saving mirrorName', updated);
                  if (updateValue) updateValue({ __updateAISummary: updated });
                }}
              />
            </div>
            
            {Array.isArray(problems) && problems.length > 0 && (
              <>
                <div className="miniPanel">
                  <label className="form-label form-label-title">{t('recap.relationshipProblems')}</label>
                </div>
                <div className="problems-list">
                {problems.map((p, idx) => {
                  console.log('🟡🔴⚫ p:', p);
                  const type = (p?.type || '');
                  // change the emoji to 3 if statements:
                  let emoji = '';
                  if (type === "notCrossedTheRedLine") {
                    emoji = '🟡';
                  } else if (type === "crossedTheRedLine") {
                    emoji = '🔴';
                  } else if (type === "blackAlert") {
                    emoji = '⚫';
                  }
                  
                  return (
                    <div key={idx} className="miniPanel problem-item">
                      <div className="problem-header">
                        <span className="problem-emoji" aria-hidden="true">{emoji}</span>
                        <span className="problem-name">{p?.name || ''}</span>
                      </div>
                      {p?.description ? (
                        <div className="problem-description">{p.description}</div>
                      ) : null}
                      
                    </div>
                    );
                  })}
                </div>
              </>
            )}
            <div className="miniPanel toggle-summary">
              <SimpleToggleSwitch
                startText={''}
                endText={t('recap.showSummary')}
                checked={showSummary}
                onToggle={() => setShowSummary((v) => !v)}
                settingsToggle
              />
            </div>
  
            {showSummary && (
              <div className="alignment-summary">
                <ConflictSummary
                  relationship={relationship}
                  mirrorID={mirrorData?.id}
                  isAlignmentStep={true}
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

export default Recap;